<script>
  import DragAndDrop from '../../components/DragAndDrop.vue';
  import FireCrawl from '../../components/FireCrawl.vue';
  import GoogleDrivePicker from '../../components/GoogleDrivePicker.vue';
  import notionLogo from '../../../../../assets/images/notion_app_logo.png'
  import { mapState, mapActions, mapGetters } from 'vuex';
  import DeepCrawl from '../../components/DeepCrawl.vue';

  export default {
    name: 'Upload',
    components: {
      DragAndDrop,
      FireCrawl,
      GoogleDrivePicker,
      DeepCrawl
    },
    data() {
      return {
        source: 'file',
        notionLogo: notionLogo,
        selectedResult: [],
        knowledgeBaseName: null,
        knowledgeEngine: 'lightrag',
        expanded: {},
        editContent: {},
        saving: {},
      }
    },
    computed: {
      ...mapState(
        'knowledge', ['isUploaded', 'crawlResult']
      ),
      ...mapGetters('stripe', ['getWordsLimit']),
      wordLimit() {
        return this.getWordsLimit || 100000;
      },
      selectedWordCount() {
        return this.selectedResult.reduce((sum, item) => {
          return sum + this.countUniversal(item.markdown || '');
        }, 0);
      },
      isWordLimitExceeded() {
        return this.selectedWordCount >= this.wordLimit;
      },
      remainingWord() {
        return this.wordLimit - this.selectedWordCount || 0;
      },
    },
    methods: {
      ...mapActions('knowledge', ['handleUpdateCrawlResult', 'handleRemoveFile', 'updateIsUploaded', 'updateKnowledgeBaseName', 'updateKnowledgeEngine', 'updateKnowledgeGraphEnabled', 'updateKnowledgeImagesEnabled', 'resetAdvancedUploadOptions']),
      isCJK(char) {
        const code = char.codePointAt(0)
        return (
          (code >= 0x4e00 && code <= 0x9fff) ||   // Chinese
          (code >= 0x3400 && code <= 0x4dbf) ||   // CJK Ext A
          (code >= 0x3040 && code <= 0x309f) ||   // Hiragana
          (code >= 0x30a0 && code <= 0x30ff) ||   // Katakana
          (code >= 0xac00 && code <= 0xd7af) ||   // Hangul
          (code >= 0x0e80 && code <= 0x0eff) ||   // Lao
          (code >= 0x1780 && code <= 0x17ff)      // Khmer
        )
      },
      isEmoji(char) {
        return /\p{Extended_Pictographic}/u.test(char)
      },
      countUniversal(text) {
        let cjkCount = 0
        let emojiCount = 0
        let buffer = ''

        for (const ch of text) {
          if (this.isCJK(ch)) {
            cjkCount++
            buffer += ' '
          } else if (this.isEmoji(ch)) {
            emojiCount++
            buffer += ' '
          } else {
            buffer += ch
          }
        }

        const words = buffer.match(/[A-Za-z]+|\d+/g) || []
        const wordCount = words.length

        return cjkCount + emojiCount + wordCount
      },
      changeSource(source) {
        this.source = source;
      },
      canSelect(result) {
        if (this.selectedResult.includes(result)) return true;

        const nextTotal =
          this.selectedWordCount + this.countUniversal(result.markdown || '');

        return nextTotal <= this.wordLimit;
      },
      handleNext(){
        this.handleUpdateCrawlResult(this.selectedResult);
        this.$router.push({name: 'add_knowledge_processing'});
      },
      handleScrollDown() {
        this.$nextTick(() => {
          const resultsEl = this.$refs.resultsList;
          if (resultsEl) {
            resultsEl.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start'
            });
          }
        });
      },
      toggleExpanded(index) {
        this.$set(this.expanded, index, !this.expanded[index]);
        if (this.expanded[index] && !this.editContent[index]) {
          this.$set(this.editContent, index, this.crawlResult.data[index].markdown || '');
        }
      },
      isEditing(index) {
        return !!this.expanded[index];
      },
      currentWordCount(index) {
        const content = this.expanded[index] && this.editContent[index] ? this.editContent[index] : this.crawlResult.data[index].markdown;
        return content ? this.countUniversal(content) : 0;
      },
      resultKey(result, index) {
        return `${result.metadata?.title || ''}-${index}`;
      },
      saveEdit(index) {
        this.$set(this.saving, index, true);
        
        // Update the markdown in crawlResult
        const updatedResult = { ...this.crawlResult.data[index] };
        updatedResult.markdown = this.editContent[index];
        
        // Update in crawlResult array
        const newData = [...this.crawlResult.data];
        newData[index] = updatedResult;

        this.$store.commit('knowledge/setCrawlResult', { data: [...newData] });
        
        // Close editor after save
        setTimeout(() => {
          this.$set(this.expanded, index, false);
          this.$set(this.saving, index, false);
        }, 300);
      },
      cancelEdit(index) {
        this.$set(this.expanded, index, false);
        this.$set(this.editContent, index, null);
      },
    },
    watch: {
      crawlResult: {
        handler(result) {
          if (!result?.data) return;

          // Reset selectedResult to empty array first to clear old selections
          this.selectedResult = [];
          
          // Then auto-select items that fit within word limit
          this.$nextTick(() => {
            const selected = [];
            let total = 0;

            for (const item of result.data) {
              const len = item.markdown?.length || 0;

              if (total + len > this.wordLimit) break;

              selected.push(item);
              total += len;
            }

            this.selectedResult = selected;
          });
          
          // Also reset expanded and editContent to clear any editing state from previous crawl
          this.expanded = {};
          this.editContent = {};
        },
        deep: true
      }
    },
    mounted(){
      this.handleRemoveFile();
      this.updateIsUploaded(false);
      this.updateKnowledgeEngine(this.knowledgeEngine);
      // Reset step 2's "Additional options" back to the defaults for a
      // fresh wizard run — Upload.vue is always the wizard's entry point.
      this.updateKnowledgeGraphEnabled(true);
      this.updateKnowledgeImagesEnabled(true);
      this.resetAdvancedUploadOptions();
    },
  }
</script>

<template>
  <div class="p-2 md:p-8 flex flex-1 h-full gap-3 flex-col m-0 bg-slate-25 dark:bg-slate-900 overflow-auto">

    <div class="flex justify-between">
      <h1 class="text-2xl text-slate-900 dark:text-slate-100">
        {{ $t('KNOWLEDGE.CREATE.UPLOAD.TITLE') }}
      </h1>
      <button
        :disabled="!isUploaded || !knowledgeBaseName"
        @click="handleNext()"
        class="inline-block bg-woot-500 text-slate-100 px-4 py-2 rounded-md hover:bg-woot-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        data-testid="upload_next_button"
      >
        {{ $t('KNOWLEDGE.CREATE.UPLOAD.NEXT') }}
      </button>
    </div>

    <div class="flex gap-2 mb-6 flex-wrap">
        <button
          @click="changeSource('file')"
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'file',
            'border-slate-200 dark:border-slate-200': source !== 'file'
          }"
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'
          data-testid="source_file_button">
            <fluent-icon icon="file-upload" class="h-[30px] w-[30px]" />
            {{ $t('KNOWLEDGE.CREATE.UPLOAD.IMPORT_FILE') }}
        </button>
        <button 
          @click="changeSource('gdrive')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'gdrive',
            'border-slate-200 dark:border-slate-200': source !== 'gdrive'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
          <img src="/assets/images/auth/google.svg" alt="Google Drive" class="h-[25px] w-[25px]" />
            {{ $t('KNOWLEDGE.CREATE.UPLOAD.IMPORT_GDRIVE') }}
        </button>
        <!-- <button 
          @click="changeSource('website')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'website',
            'border-slate-200 dark:border-slate-200': source !== 'website'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
            <fluent-icon icon="globe" class="h-[25px] w-[25px]" />
            {{ $t('KNOWLEDGE.CREATE.UPLOAD.SYNC_WEBSITE') }}
        </button> -->
        <button 
          @click="changeSource('deepcrawl')" 
          :class="{
            'text-woot-500 border-woot-500 dark:bg-slate-800 dark:border-woot-500': source === 'deepcrawl',
            'border-slate-200 dark:border-slate-200': source !== 'deepcrawl'
          }" 
          class='border-2 p-3 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 flex gap-2 items-center'>
            <fluent-icon icon="globe" class="h-[25px] w-[25px]" />
            Sync from website
        </button>
    </div>

    <div>
      <span class="flex gap-2 items-center">
        <h1><span class="font-bold text-red-500">*</span>{{ $t('KNOWLEDGE.CREATE.UPLOAD.NAME_LABEL') }}</h1>
        <p class="text-xs m-0 p-0">{{ $t('KNOWLEDGE.CREATE.UPLOAD.NAME_MAX_LENGTH') }}</p>
      </span>
      <input type="text" maxlength="17" :placeholder="$t('KNOWLEDGE.CREATE.UPLOAD.NAME_PLACEHOLDER')" v-model="knowledgeBaseName" @change="updateKnowledgeBaseName(knowledgeBaseName)" data-testid="knowledge_name_input"/>
    </div>

    <div class="my-6" v-if="source === 'file'">
      <h1>{{ $t('KNOWLEDGE.CREATE.UPLOAD.FILE_SECTION') }}</h1>
      <div class="my-4">
          <DragAndDrop />
      </div>
    </div>

    <div class="my-6" v-if="source === 'gdrive'">
      <h1>{{ $t('KNOWLEDGE.CREATE.UPLOAD.GDRIVE_SECTION') }}</h1>
      <div class="my-4">
          <GoogleDrivePicker />
      </div>
    </div>

    <div v-if="source === 'website'">
      <h1>{{ $t('KNOWLEDGE.CREATE.UPLOAD.WEBSITE_SECTION') }}</h1>
      <div class="my-4 ">
        <FireCrawl @scrollDown="handleScrollDown"/>
      </div>
    </div>

    <div v-if="source === 'deepcrawl'">
      <h1>Website</h1>
      <div class="my-4">
        <DeepCrawl @scrollDown="handleScrollDown" />
      </div>
    </div>

    <div
      v-if="crawlResult && crawlResult.data && crawlResult.data.length"
      class="text-sm text-gray-600 mb-3"
    >
      {{ selectedWordCount.toLocaleString() }} /
      {{ wordLimit.toLocaleString() }} words used
    </div>

    <div
      v-if="isWordLimitExceeded"
      class="flex items-start gap-2 p-4 mb-4 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
    >
      <fluent-icon icon="warning" class="w-5 h-5 mt-[2px]" />
      <div>
        <p class="font-medium">
          Word limit reached
        </p>
        <p class="mt-1">
          You’ve reached the maximum allowed word limit.
          Deselect some content to select more results.
        </p>
      </div>
    </div>

    <div v-if="crawlResult && crawlResult.data && crawlResult.data.length" ref="resultsList" class="space-y-4 break-all">
      <div
        v-for="(result, index) in crawlResult.data"
        :key="resultKey(result, index)"
        class="border overflow-hidden border-gray-300 rounded-lg shadow-sm p-4 flex flex-col gap-3 transition-shadow duration-300"
        :class="{
          'cursor-not-allowed': !canSelect(result),
          'hover:shadow-md': canSelect(result)
        }"
      >
        <div class="flex items-start gap-4">
          <!-- checkbox -->
          <div
            class="mt-1"
            :class="!canSelect(result) ? 'opacity-50' : ''"
          >
            <input
              type="checkbox"
              v-model="selectedResult"
              :value="result"
              class="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-400"
              :disabled="!canSelect(result) || isEditing(index)"
              :class="{
                'cursor-not-allowed': !canSelect(result),
                'hover:shadow-md': canSelect(result)
              }"
            />
          </div>

          <!-- main info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <div class="min-w-0">
                <h1 class="text-lg font-semibold text-gray-800 truncate">{{ result.metadata.title }}</h1>
                <p class="text-sm text-gray-500 mb-1 truncate">{{ result.metadata.description }}</p>
              </div>

              <!-- expand / edit arrow -->
              <woot-button 
                variant="clear" 
                icon="edit" 
                @click.stop="toggleExpanded(index)"
                :aria-expanded="!!expanded[index]"
                title="Expand to edit"
              > {{ expanded[index] ? 'Close' : 'Edit' }}</woot-button>
            </div>

            <div class="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span>Word count: {{ currentWordCount(index) }}</span>
            </div>

            <p
              v-if="!canSelect(result)"
              class="text-xs text-red-500 flex items-center gap-1 mt-1"
            >
              <fluent-icon icon="info" size="12" />
              Cannot select: exceeds allowed word limit. You can still edit this chunk to make it selectable.
            </p>

            <hr class="my-2 border-gray-200" />

            <!-- read-only preview -->
            <p v-if="!expanded[index]" class="line-clamp-3 text-gray-700 text-sm whitespace-pre-wrap break-words">
              {{ result.markdown }}
            </p>

            <!-- editor when expanded -->
            <div v-else class="w-full">
              <textarea
                v-model="editContent[index]"
                class="w-full p-3 rounded border border-gray-200
                  focus:outline-none focus:ring-2 focus:ring-blue-200
                  text-sm text-gray-800 whitespace-pre-wrap
                  h-48 resize-none"
                @click.stop
                :maxlength="remainingWord"
              ></textarea>

              <div class="mt-2 flex items-center gap-2">
                <button
                  class="px-3 py-1 rounded border text-sm"
                  @click.stop="cancelEdit(index)"
                  :disabled="saving[index]"
                >
                  Cancel
                </button>

                <button
                  class="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                  @click.stop="saveEdit(index)"
                  :disabled="saving[index] || remainingWord === 0"
                >
                  {{ saving[index] ? 'Saving...' : 'Save' }}
                </button>

                <span class="ml-auto text-xs text-gray-500">Chars: {{ editContent[index] ? countUniversal(editContent[index]) : 0 }}</span>
                <span v-if="remainingWord === 0" class="ml-auto text-xs text-red-500">Word limit exceeded</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.truncate-multiline {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
